import type { ButtonHTMLAttributes, ReactNode } from 'react';
import './Button.css';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  /**
   * Work is in flight, and this button is REPORTING it rather than offering an
   * action. Non-interactive like `disabled`, but at full strength: a disabled
   * button says "you may not", and dimming one that is showing live progress
   * reads as broken rather than busy.
   *
   * Distinct from `loading`, which replaces the label with a spinner. Use
   * `busy` when the label is the message ("Reading your answers…").
   */
  busy?: boolean;
  icon?: ReactNode;
  children: ReactNode;
}

export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  busy = false,
  icon,
  children,
  disabled,
  className = '',
  ...props
}: ButtonProps) {
  return (
    <button
      className={`btn btn-${variant} btn-${size} ${fullWidth ? 'btn-full' : ''} ${busy ? 'btn-busy' : ''} ${className}`}
      // Genuinely disabled — a CSS-only block would stop the mouse and let the
      // keyboard straight through. `.btn-busy` takes back only the DIMMING.
      disabled={disabled || loading || busy}
      aria-busy={loading || busy || undefined}
      {...props}
    >
      {loading ? (
        // Spinner only — render label as visually-hidden so the button
        // keeps its natural width and screen readers still announce the
        // action; visible label would crowd against the spinner.
        <>
          <span className="btn-spinner" aria-hidden="true" />
          <span className="btn-text-sr-only">{children}</span>
        </>
      ) : (
        <>
          {icon && <span className="btn-icon">{icon}</span>}
          <span className="btn-text">{children}</span>
        </>
      )}
    </button>
  );
}
